import { useCallback, useEffect, useRef } from 'react';

/**
 * Calls function in given timeout
 * @param callback - unction that will be called after the timer elapses
 * @param delay - number of milliseconds the timer should wait before the specified function is executed
 * @param options: { autoInvoke } - determines whether the timer should be started on mount, defaults to false
 * @returns result - object with start and clear functions
 * start - starts the timer
 * clear – cancels the timer   
 * 
 * @api
 * ```tsx
 *  const { start, clear } = useTimeout(callback, delay, {
        autoInvoke: true,
    });
 * ```
 * @example
 * ```tsx
    import { useState } from 'react';
    import { randomId, useTimeout } from '@intigral/hooks';

    function Demo() {
    const [value, setValue] = useState('');
    const { start, clear } = useTimeout(() => setValue(randomId()), 1000);

    return (
        <Group>
        <Button onClick={start}>Start</Button>
        <Button onClick={clear} color="red">
            Clear
        </Button>
        <Text>Random value: {value}</Text>
        </Group>
    );
    }
    ```
    @classdesc
    useTimeout is a hook that calls a function in a given timeout. It returns an object with start and clear functions.
    ```tsx
    function useTimeout(
        callback: (...callbackParams: any[]) => void,
        delay: number,
        options?: {
            autoInvoke: boolean;
        }
        ): {
        start: (...callbackParams: any[]) => void;
        clear: () => void;
    };
    ```
 */

export function useTimeout(
    callback: (...callbackParams: any[]) => void,
    delay: number,
    options: { autoInvoke: boolean } = { autoInvoke: false }
) {
    const timeoutRef = useRef<number | null>(null);

    const start = useCallback(
        (...callbackParams: any[]) => {
            if (!timeoutRef.current) {
                timeoutRef.current = window.setTimeout(() => {
                    callback(callbackParams);
                    timeoutRef.current = null;
                }, delay);
            }
        },
        [delay]
    );

    const clear = useCallback(() => {
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (options.autoInvoke) {
            start();
        }

        return clear;
    }, [clear, start]);

    return { start, clear };
}
